/* Teeinblue SDK adapter, source audited 2026-09-28.
 * Implements SDK md/tZ preparation only. Never writes the Shopify cart.
 * The caller sends primary and selected cross-sell items in ONE cart/add request.
 */
(function (global) {
  'use strict';
  const api = 'https://api.teeinblue.com/api';
  const cdn = 'https://cdn.teeinblue.com';
  const errors = {
    de: 'Das Zusatzprodukt konnte nicht sicher gespeichert werden. Bitte erneut versuchen.',
    en: 'The additional product could not be saved safely. Please try again.',
    fr: 'Le produit supplémentaire n’a pas pu être enregistré. Veuillez réessayer.',
    es: 'No se ha podido guardar el producto adicional. Inténtalo de nuevo.',
    it: 'Non è stato possibile salvare il prodotto aggiuntivo. Riprova.'
  };
  function fail() { return new Error(errors[(global.document?.documentElement.lang || 'en').split('-')[0]] || errors.en); }
  function snapshot(selection) { return selection ? JSON.parse(JSON.stringify(selection)) : null; }
  function artworkExtension(format) {
    return ['svg', 'eps', 'ai', 'jpeg', 'pdf'].includes(format) ? format :
      ['jpeg_cmyk', 'jpeg_cmyk_coated_fogra39'].includes(format) ? 'jpeg' : 'png';
  }
  async function effectiveConfig(selection, options) {
    // Explicit config is reserved for the caller/test supplying the full effective SDK config.
    if (options.config) return options.config;
    const campaign = global.teeinblueCampaign || global.TeeInBlueCampaign || {};
    if (!campaign.config || typeof campaign.config !== 'object') throw fail();
    const config = { ...campaign.config, ...(campaign.configTranslation || {}) };
    // CampaignInstance.getCampaign overrides SKU from the API campaign, not the
    // global theme config. Obtain it only where this field affects production.
    if (selection.product?.isCampByProductBase === true && selection.fulfillmentLinkedId) {
      const shop = options.shop || global.Shopify?.shop;
      const productId = String(campaign.productId || '').split('/').pop();
      if (!shop || !/^\d+$/.test(productId)) throw fail();
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await (options.fetch || global.fetch)(`${api}/merchant/campaigns/${productId}.json?shop=${encodeURIComponent(shop)}&v=${encodeURIComponent(campaign.version || 1)}`, { signal: controller.signal });
        if (!response.ok) throw fail();
        const loaded = await response.json();
        if (!loaded || typeof loaded !== 'object' || !loaded.id) throw fail();
        config.sku_enabled = loaded.sku_enabled ?? false;
      } catch (_) { throw fail(); } finally { clearTimeout(timeout); }
    }
    return config;
  }
  async function prepare(selection, parentId, options = {}) {
    if (!selection) return null;
    const { product, selectedVariantId, customizationData, personalizedData } = selection;
    const quantity = Number(product?.quantity || 1);
    const variant = String(selectedVariantId || '').split('/').pop();
    const productId = String(product?.id || '').split('/').pop();
    if (!parentId || !/^\d+$/.test(variant) || !/^\d+$/.test(productId) || !Number.isInteger(quantity) || quantity < 1) throw fail();
    const config = await effectiveConfig(selection, options);
    if (!config || typeof config !== 'object') throw fail();
    const props = { _parent_customization_id: parentId, _is_sub_item: true };
    // SDK md uses a plain variant only when there is no customization data.
    if (!customizationData) return { id: variant, quantity, properties: props };
    const isProductBase = product.isCampByProductBase === true;
    const designs = isProductBase ? selection.designPrintareas : selection.designArtworks;
    if (!Array.isArray(designs)) throw fail();
    const shop = options.shop || global.Shopify?.shop;
    if (!shop) throw fail();
    const endpoint = isProductBase ?
      `merchant/shopify/customization?shop=${encodeURIComponent(shop)}&id=${encodeURIComponent(productId)}` :
      `merchant/platform-products/${encodeURIComponent(productId)}/platform-customizations?shop=${encodeURIComponent(shop)}`;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 60000);
    let saved;
    try {
      const response = await (options.fetch || global.fetch)(`${api}/${endpoint}`, {
        method: 'POST', signal: controller.signal,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: { ...customizationData }, platform_variant_id: selectedVariantId })
      });
      if (!response.ok) throw fail();
      saved = await response.json();
      if (!saved?.id || !/^[\w-]+$/.test(String(saved.id))) throw fail();
    } catch (_) { throw fail(); } finally { clearTimeout(timeout); }
    // SDK YX waits for the configured platform-customization delay before cart use.
    if (!isProductBase) await new Promise(resolve => setTimeout(resolve, (global.teeinblue || global.TeeInBlue || {}).customization_delay || 1000));
    if (isProductBase) {
      // SDK QX: fulfillment keys from text, numeric and date fields only.
      for (const field of selection.availableFields || []) {
        if (!['Text', 'InputNumber', 'DatePickerInput'].includes(field.type) || !field.fulfilledKeyEnabled || !field.fulfilledKey?.length) continue;
        const value = customizationData[field.name];
        if (value && customizationData[field.name + '-visibility'] !== false) props['_tibstr_' + field.fulfilledKey] = value;
      }
    }
    Object.assign(props, personalizedData || {}, { _customization_id: saved.id, _parent_customization_id: parentId, _is_sub_item: true });
    if (isProductBase && selection.fulfillmentLinkedId && config.sku_enabled) props._tib_fulfilled_sku = selection.fulfillmentLinkedId;
    if (config.support_public_property !== false) props.customization_id = saved.id;
    if (config.is_display_customization_link !== false) props._customization_image = `${cdn}/customizations/${saved.id}.jpg`;
    if (config.is_display_customization_edit_link === true) {
      const edit = new URL(global.location.href);
      edit.searchParams.set('customization-id', saved.id);
      props._customization_edit_link = edit.toString();
    }
    if (!config.add_design_links || ['property', 'both'].includes(config.add_design_links)) {
      designs.forEach((design, index) => {
        if (isProductBase) {
          if (!design.id || !design.designExt) throw fail();
          let link = `${cdn}/designs/${saved.id}/${saved.id}-${design.id}.${design.designExt}`;
          if (design.name) link += '?printarea=' + design.name.replace(/\s+/g, '-').toLowerCase();
          props['_tib_design_link_' + (index + 1)] = link;
        } else {
          if (!design.key || !design.artwork?.id) throw fail();
          props[design.key] = `${cdn}/designs/${saved.id}/${saved.id}-${design.artwork.id}.${artworkExtension(design.artwork.design_format)}`;
        }
      });
    }
    return { id: variant, quantity, properties: props };
  }
  function verify(expected, cart) {
    return expected.every(item => cart.items.filter(line =>
      String(line.variant_id || line.id) === String(item.id) &&
      String(line.properties?._customization_id || '') === String(item.properties?._customization_id || '') &&
      String(line.properties?._parent_customization_id || '') === String(item.properties?._parent_customization_id || '')
    ).reduce((sum, line) => sum + line.quantity, 0) === item.quantity);
  }
  global.UWSPairCrosssell = { snapshot, prepare, verify };
})(window);
